'use client'
import { useState, useEffect } from 'react'
import { useTranslations, useLocale } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useAuthStore } from '@/src/shared/lib/store/auth-store'
import { useNavigation } from '@/src/shared/lib/hooks/use-navigation'
import { Logo } from '@/src/shared/ui/Logo'
import { Navigation } from '@/src/shared/ui/navigation'
import { LanguageSelector } from '@/src/features/language-selector'
import { UserMenu } from '@/src/features/user-menu'
import { MobileMenu } from './ui/MobileMenu'
import { PageBackground } from '../../shared/ui/background'
import { Menu, X } from 'lucide-react'
import { LoginButton } from '@/src/features/auth'
import { NotificationBell } from '@/src/widgets/notifications'

export function Header() {
  const t = useTranslations('header')
  const locale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [activeSection, setActiveSection] = useState<string>('')
  const { isAuthenticated, user } = useAuthStore()
  const { navItems } = useNavigation()

  // Landing page (e.g. "/en") — enable background parallax only here
  const isHomePage = pathname === `/${locale}` || pathname === `/${locale}/` || pathname === '/'

  // Scroll listener for header background transition and active section detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      // Calculate scroll progress from 0 to 1 over 50px
      const progress = Math.min(scrollY / 50, 1)
      setScrollProgress(progress)

      // Detect active section
      const sections = ['hero', 'categories', 'post-task', 'contractors', 'how-it-works', 'become-contractor']
      const sectionOffsets = sections
        .map(id => {
          const element = document.getElementById(id)
          if (element) {
            const rect = element.getBoundingClientRect()
            return { id, offset: rect.top + scrollY, height: rect.height }
          }
          return null
        })
        .filter(Boolean) as { id: string; offset: number; height: number }[]
      const headerOffset = 100
      const currentScroll = scrollY + headerOffset
      for (let i = sectionOffsets.length - 1; i >= 0; i--) {
        const section = sectionOffsets[i]
        if (currentScroll >= section.offset) {
          setActiveSection(section.id)
          break
        }
      }
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const availableLocales = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ]

  const switchLocale = (newLocale: string) => {
    const pathWithoutLocale = pathname.replace(`/${locale}`, '')
    const newPathname = `/${newLocale}${pathWithoutLocale}`
    router.push(newPathname)
  }

  const headerStyle = {
    backgroundColor: `rgba(255, 255, 255, ${scrollProgress})`,
    borderColor: `rgba(229, 231, 235, ${scrollProgress})`,
    boxShadow:
      scrollProgress > 0.5
        ? '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
        : 'none'
  }

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full border-b transition-shadow duration-300"
        style={headerStyle}
      >
        <div className="mx-auto px-[40px] sm:px-6 py-[18px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Logo locale={locale} />
              <div className="hidden lg:flex">
                <Navigation
                  items={navItems}
                  pathname={pathname}
                  activeSection={activeSection}
                />
              </div>
            </div>
            <div className="hidden items-center gap-4 lg:flex">
              {isAuthenticated ? (
                <>
                  <NotificationBell locale={locale} />
                  <UserMenu user={user!} locale={locale} />
                </>
              ) : (
                <LoginButton label={t('signInSignUp')} />
              )}
              <LanguageSelector
                currentLocale={locale}
                availableLocales={availableLocales}
                onLocaleChange={switchLocale}
                label={t('language')}
              />
            </div>
            <div className="flex items-center gap-3 lg:hidden">
              {!isAuthenticated && (
                <LoginButton label={t('signInSignUp')} />
              )}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="rounded-lg p-2 text-text-primary transition-colors hover:bg-background2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
        <MobileMenu
          isOpen={isMobileMenuOpen}
          navItems={navItems}
          pathname={pathname}
          locale={locale}
          availableLocales={availableLocales}
          isAuthenticated={isAuthenticated}
          user={user ?? undefined}
          activeSection={activeSection}
          onClose={() => setIsMobileMenuOpen(false)}
          onLocaleChange={switchLocale}
          languageLabel={t('language')}
          signInLabel={t('signInSignUp')}
        />
      </header>
      <div className="w-full h-0 z-[-1]">
        <PageBackground parallax={isHomePage} />
      </div>
    </>
  )
}